import { useEffect, useState } from 'react';

import { Input } from '../../core/input/Input';
import type { ClarifyOption } from './types';

export type InChatClarifyCardProps = {
    readonly options: readonly ClarifyOption[];
    readonly onSelect: (value: string) => void;
    readonly disabled?: boolean;
    readonly otherPlaceholder?: string;
};

const letterAt = (index: number): string => String.fromCharCode(65 + index);

export function InChatClarifyCard({
    options,
    onSelect,
    disabled = false,
    otherPlaceholder = 'Other…',
}: InChatClarifyCardProps) {
    const [customText, setCustomText] = useState('');

    useEffect(() => {
        if (disabled) return;
        const handleKey = (event: KeyboardEvent): void => {
            if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
            const idx = event.key.toUpperCase().charCodeAt(0) - 65;
            const option = options[idx];
            if (option === undefined) return;
            event.preventDefault();
            onSelect(option.headline);
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [disabled, options, onSelect]);

    const submitCustom = (): void => {
        const value = customText.trim();
        if (value.length === 0) return;
        onSelect(value);
        setCustomText('');
    };

    return (
        <div className="flex w-full flex-col gap-s font-sans">
            {options.map((option, index) => (
                <button
                    key={option.headline}
                    type="button"
                    disabled={disabled}
                    className="group/clarify-option flex w-full items-start gap-m rounded-xl border-0 bg-gray-100 px-xl py-l text-left font-sans transition duration-200 ease enabled:cursor-pointer enabled:hover:bg-gray-200 disabled:cursor-default disabled:opacity-60"
                    onClick={() => onSelect(option.headline)}
                >
                    <span className="mt-xxs flex h-5 w-5 shrink-0 items-center justify-center rounded-s bg-gray-300 text-xxs font-semibold text-black-500 transition duration-200 ease group-enabled/clarify-option:group-hover/clarify-option:bg-gray-400">
                        {letterAt(index)}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                        <span className="text-sm font-medium leading-snug text-black-800">{option.headline}</span>
                        {option.subline !== undefined && option.subline.length > 0 ? (
                            <span className="text-xxs leading-snug text-black-500">{option.subline}</span>
                        ) : null}
                    </span>
                </button>
            ))}
            <div className="flex items-center gap-m rounded-xl bg-gray-100 px-xl py-l transition duration-200 ease hover:bg-gray-200">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-s bg-gray-300 text-xxs font-semibold text-black-400">
                    {letterAt(options.length)}
                </span>
                <Input
                    value={customText}
                    onChange={(event) => setCustomText(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && customText.trim().length > 0) {
                            event.preventDefault();
                            submitCustom();
                        }
                    }}
                    placeholder={otherPlaceholder}
                    disabled={disabled}
                    className="!h-5 flex-1 !border-0 !bg-transparent !p-0 !text-sm !font-medium !text-black-700 !shadow-none !outline-none placeholder:!font-medium placeholder:!text-black-400"
                />
            </div>
        </div>
    );
}
